import { Link } from "react-router-dom";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HERO_IMG } from "@/lib/categories";
import { BRAND } from "@/lib/brand";
import { HeartHandshake, Baby, Activity, FolderHeart, ArrowRight } from "lucide-react";

const PROGRAMS = [
  { Icon: HeartHandshake, title: "Community Health Screenings",
    body: "Bringing health education and screenings to barbershops, salons, and trusted gathering spaces across Colorado." },
  { Icon: Baby, title: "Maternal Mental Health",
    body: "Advocacy, education, and support for Black mothers and families navigating perinatal mental wellness." },
  { Icon: Activity, title: "Sports-Based Youth Wellness",
    body: "Mental-health resilience programs designed for Black and BIPOC youth athletes — body, mind, and community." },
  { Icon: FolderHeart, title: "The Resource Directory",
    body: "This platform — connecting Coloradans with culturally aligned providers, organizations, and ministries." },
];

export default function AboutPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/subscribers", { email, source: "about_page" });
      toast.success("You're on the list! Check your inbox soon.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <picture>
            <source srcSet="/brand/hero-colorado.webp" type="image/webp" />
            <img src="/brand/hero-colorado.jpg" alt="" width="1584" height="672" className="h-full w-full object-cover" loading="eager" decoding="async" />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/85 via-primary/75 to-primary/40" />
        </div>
        <div className="container-page relative z-10 py-20 text-primary-foreground sm:py-24">
          <span className="chip mb-5 bg-accent/95 text-accent-foreground">About us</span>
          <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Health equity, built by and for Colorado's Black community.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-primary-foreground/90">
            Black Health Resources is a program of the Colorado Black Health Collaborative — a nonprofit
            dedicated to making culturally aligned care visible, accessible, and trusted.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="container-page py-20" data-testid="about-mission">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">Our Mission</span>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-primary lg:text-4xl">
              A living, searchable directory of Black-led care across Colorado.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-foreground/80">
              The Colorado Black Health Collaborative (CBHC) is a nonprofit dedicated to achieving health
              equity for Colorado's Black and African American communities. Black Health Resources is our
              living, searchable directory of the providers, organizations, and ministries serving that
              mission across the state — from Fort Collins to Pueblo.
            </p>
          </div>
          <div className="relative">
            <img
              src={BRAND.community}
              alt="Colorado community at a wellness gathering"
              className="aspect-[4/3] w-full rounded-2xl object-cover shadow-[0_20px_60px_-20px_rgba(13,71,68,0.45)]"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="bg-sand/40 py-20" data-testid="about-programs">
        <div className="container-page">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">What We Do</span>
          <h2 className="mt-3 font-display text-3xl font-bold text-primary lg:text-4xl">Our programs</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {PROGRAMS.map(({ Icon, title, body }) => (
              <div key={title} className="card-surface p-6">
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary/8 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold text-primary">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/80">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="container-page py-20" data-testid="about-why">
        <div className="mx-auto max-w-3xl">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">Why It Matters</span>
          <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-primary lg:text-4xl">
            A legacy of resilience, resistance, and restoration.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-foreground/80">
            When formal health systems were inaccessible, Black communities built their own — through
            churches, mutual-aid networks, and extended families. Today, Black clinicians and advocates
            continue that work. We exist to make them findable, claimable, and supportable.
          </p>
        </div>
      </section>

      {/* Get Involved */}
      <section className="container-page pb-24" data-testid="about-cta">
        <div className="overflow-hidden rounded-3xl bg-primary p-10 text-primary-foreground sm:p-14">
          <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr] lg:items-center">
            <div>
              <span className="chip bg-accent/95 text-accent-foreground">Get Involved</span>
              <h3 className="mt-4 font-display text-3xl font-bold leading-tight sm:text-4xl">
                Join the directory. Join the mission.
              </h3>
              <p className="mt-3 max-w-xl text-primary-foreground/80">
                List your practice or organization in minutes — or partner with us at{" "}
                <a href="mailto:partners@coloradoblackhealth.org" className="text-accent underline-offset-4 hover:underline">
                  partners@coloradoblackhealth.org
                </a>.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/directory" className="btn-accent" data-testid="about-cta-claim">
                  Claim your listing <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/signup" className="btn-secondary !bg-white/10 !text-primary-foreground hover:!bg-white/20" data-testid="about-cta-signup">
                  List your practice
                </Link>
              </div>
            </div>
            <form onSubmit={subscribe} className="space-y-3" data-testid="about-newsletter-form">
              <label className="block text-xs font-semibold uppercase tracking-wider text-primary-foreground/80">
                Subscribe to community updates
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full rounded-md bg-primary-foreground/10 px-4 py-3 text-primary-foreground placeholder:text-primary-foreground/50 outline-none ring-1 ring-primary-foreground/20 focus:ring-accent"
                data-testid="about-newsletter-email"
              />
              <button type="submit" disabled={submitting} className="btn-accent w-full !py-3" data-testid="about-newsletter-submit">
                {submitting ? "Sending…" : "Subscribe"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
